import React, { useState, useLayoutEffect, useEffect } from 'react';

const TOUR_KEY = 'ss_tour_completed';

interface TourStep {
  selector: string;
  title: string;
  text: string;
}

function getStepsForPlan(): TourStep[] {
  const inventarioText =
    'Gestiona tus productos químicos con clasificación SGA y crea tu bodega verificando incompatibilidades en almacenamiento (NTE INEN 2266:2013, 6.1.7.10 b)).';

  return [
    { selector: '[data-tour="tour-sidebar"]',    title: 'Menú principal',     text: 'Aquí encuentras todos los módulos de StorSafe.' },
    { selector: '[data-tour="tour-inventario"]', title: 'Inventario',         text: inventarioText },
    { selector: '[data-tour="tour-checklist"]',  title: 'Primeros pasos',     text: 'Sigue el checklist de primeros pasos para completar tu configuración.' },
    { selector: '[data-tour="tour-profile"]',    title: 'Tu cuenta',          text: 'Accede a tu perfil y configuración.' },
  ];
}

const PADDING = 8;
const TOOLTIP_WIDTH = 300;

export default function OnboardingTour() {
  const STEPS = getStepsForPlan();
  const [active, setActive] = useState(() => localStorage.getItem(TOUR_KEY) !== 'true');
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const updateRect = () => {
    const el = document.querySelector(STEPS[step].selector);
    setRect(el ? el.getBoundingClientRect() : null);
  };

  useLayoutEffect(() => {
    if (!active) return;
    updateRect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, step]);

  useEffect(() => {
    if (!active) return;
    window.addEventListener('resize', updateRect);
    return () => window.removeEventListener('resize', updateRect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, step]);

  if (!active) return null;

  function finish() {
    localStorage.setItem(TOUR_KEY, 'true');
    setActive(false);
  }

  function handleNext() {
    if (step === STEPS.length - 1) { finish(); return; }
    setStep((s) => s + 1);
  }

  const current = STEPS[step];

  const highlightStyle: React.CSSProperties = rect
    ? {
        position: 'fixed',
        top: rect.top - PADDING,
        left: rect.left - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
        borderRadius: 10,
        boxShadow: '0 0 0 9999px rgba(0,0,0,0.65)',
        zIndex: 1200,
        pointerEvents: 'none',
        transition: 'all 0.25s ease',
      }
    : {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        zIndex: 1200,
        pointerEvents: 'none',
      };

  const tooltipTop = rect
    ? (rect.bottom + 180 < window.innerHeight ? rect.bottom + 14 : Math.max(14, rect.top - 170))
    : window.innerHeight / 2 - 90;
  const tooltipLeft = rect
    ? Math.min(Math.max(14, rect.left), window.innerWidth - TOOLTIP_WIDTH - 14)
    : window.innerWidth / 2 - TOOLTIP_WIDTH / 2;

  return (
    <>
      <div style={highlightStyle} />
      <div style={{ ...tooltip, top: tooltipTop, left: tooltipLeft }}>
        <p style={tooltipTitle}>{current.title}</p>
        <p style={tooltipText}>{current.text}</p>
        <div style={dotsRow}>
          {STEPS.map((_, i) => (
            <span key={i} style={{ ...dot, ...(i === step ? dotActive : {}) }} />
          ))}
        </div>
        <div style={actionsRow}>
          <button style={skipBtn} onClick={finish}>Saltar tour</button>
          <button style={nextBtn} onClick={handleNext}>
            {step === STEPS.length - 1 ? 'Finalizar' : 'Siguiente →'}
          </button>
        </div>
      </div>
    </>
  );
}

const tooltip: React.CSSProperties = {
  position: 'fixed',
  zIndex: 1201,
  background: '#fff',
  borderRadius: 12,
  padding: '18px 20px',
  width: TOOLTIP_WIDTH,
  boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
};

const tooltipTitle: React.CSSProperties = {
  margin: '0 0 6px',
  fontSize: '1rem',
  fontWeight: 700,
  color: '#0f172a',
};

const tooltipText: React.CSSProperties = {
  margin: '0 0 14px',
  fontSize: '0.86rem',
  color: '#475569',
  lineHeight: 1.5,
};

const dotsRow: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  marginBottom: 14,
};

const dot: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: '#cbd5e1',
};

const dotActive: React.CSSProperties = {
  background: '#2563eb',
};

const actionsRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const skipBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#94a3b8',
  fontSize: '0.8rem',
  fontWeight: 600,
  cursor: 'pointer',
  padding: '6px 0',
};

const nextBtn: React.CSSProperties = {
  background: '#2563eb',
  border: 'none',
  color: '#fff',
  fontSize: '0.84rem',
  fontWeight: 700,
  borderRadius: 8,
  padding: '8px 16px',
  cursor: 'pointer',
};
